/**
 * **Where one client stands with their trainer** — booking-system R11, R16: two figures off one
 * response (`balance`, and the server's own `sessionsOwed`), read as ONE standing and said as one
 * phrase wherever it is drawn: the booking screen's session panel and its Rozliczenia tab, the
 * client card's „Rozliczenie" band, and the roster's column.
 *
 * Below zero a balance is SESSIONS TO SETTLE — never a debt and never a minus sign (R16).
 *
 * Pure: no React, no DOM, no query hook, no i18next — `t` arrives as a parameter.
 */

import type { TFunction } from 'i18next';

const LEDGER = 'web:booking.ledger';

/** The client's standing: sessions to settle when below zero, else the credits left. */
export type CreditStanding =
  | { readonly kind: 'owed'; readonly count: number }
  | { readonly kind: 'balance'; readonly count: number };

export function creditStanding(row: {
  readonly balance: number;
  readonly sessionsOwed: number;
}): CreditStanding {
  return row.sessionsOwed > 0
    ? { kind: 'owed', count: row.sessionsOwed }
    : { kind: 'balance', count: Math.max(0, row.balance) };
}

/**
 * A balance the server published WITHOUT its `sessionsOwed` — a history line's `balanceAfter`, or
 * one the app projects for an act not yet committed — read as a standing. Owed is the balance
 * below zero, turned over: `CreditBalance.SessionsOwed`'s own rule, and the only place this app
 * restates it.
 */
export function standingOfBalance(balance: number): CreditStanding {
  return creditStanding({ balance, sessionsOwed: Math.max(0, -balance) });
}

/** R11, as mobile reads it (`OWED_WARNING_THRESHOLD`): from two owed sessions a reading warns. */
const OWED_WARNING_THRESHOLD = 2;

/** Whether the standing is a warning rather than a reading — one owed session is not yet. */
export function standingWarns(standing: CreditStanding): boolean {
  return standing.kind === 'owed' && standing.count >= OWED_WARNING_THRESHOLD;
}

// ── How a standing is said ────────────────────────────────────────────────────────────────────────────────

/** „3 sesje do rozliczenia", „4 żetony" — one standing as a phrase. */
export function standingPhrase(standing: CreditStanding, t: TFunction): string {
  const unit =
    standing.kind === 'owed'
      ? t(`${LEDGER}.owedUnit`, { count: standing.count })
      : t(`${LEDGER}.creditUnit`, { count: standing.count });
  return t(`${LEDGER}.counted`, { count: standing.count, unit });
}

/** The noun under the ledger's big figure — „sesje do rozliczenia", „żetony". */
export function standingUnit(standing: CreditStanding, t: TFunction): string {
  return standing.kind === 'owed'
    ? t(`${LEDGER}.owedUnit`, { count: standing.count })
    : t(`${LEDGER}.creditUnit`, { count: standing.count });
}

/** The history's „Saldo po" column: „3 do rozl." below zero, the bare balance otherwise. */
export function standingShort(standing: CreditStanding, t: TFunction): string {
  return standing.kind === 'owed'
    ? t('web:booking.credits.ledger.owedShort', { count: standing.count })
    : String(standing.count);
}

/**
 * „+10", „−1", „0" — a standing in the room of a table cell: the roster's column (owner, 27
 * September 2026). Sessions to settle are a minus (the typographic one, as the ledger's changes),
 * credits in hand a plus; the full phrase (`standingPhrase`) goes with it as the cell's name, so
 * nothing is said only in a sign.
 */
export function standingSigned(standing: CreditStanding): string {
  if (standing.kind === 'owed') return `\u2212${String(standing.count)}`;
  return standing.count > 0 ? `+${String(standing.count)}` : '0';
}
